import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Play, Trophy, Trash2, Settings, Save } from "lucide-react";

export default function AdminSelection() {
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState("");
  const [officers, setOfficers] = useState("");
  const [numReserves, setNumReserves] = useState(3);
  const [running, setRunning] = useState(false);
  const [settings, setSettings] = useState({ next_empowerment_selection: "", ben1m_blackout_months: 24, ben200k_blackout_months: 12 });

  const load = async () => {
    const [s, cfg] = await Promise.all([
      api.get("/admin/empowerment/selections"),
      api.get("/admin/programme-settings"),
    ]);
    setItems(s.data);
    setSettings({
      next_empowerment_selection: cfg.data.next_empowerment_selection || "",
      ben1m_blackout_months: cfg.data.ben1m_blackout_months ?? 24,
      ben200k_blackout_months: cfg.data.ben200k_blackout_months ?? 12,
    });
  };
  useEffect(() => {
    document.title = "Admin · Empowerment selection";
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, "0");
    setMonth(`${y}-${m}`);
    load();
  }, []);

  const run = async () => {
    if (!month.match(/^\d{4}-\d{2}$/)) { toast.error("Month must be YYYY-MM."); return; }
    if (!window.confirm(`Run selection for ${month}? This is irreversible in terms of blackout counters.`)) return;
    setRunning(true);
    try {
      await api.post("/admin/empowerment/run-selection", { month, officers, num_reserves: Number(numReserves) });
      toast.success("Selection completed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setRunning(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this selection? Previous-award blackouts on selected beneficiaries will also be removed.")) return;
    await api.delete(`/admin/empowerment/selections/${id}`); toast.success("Deleted"); load();
  };

  const saveSettings = async () => {
    try { await api.put("/admin/programme-settings", settings); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="admin-selection">
      <h1 className="font-serif text-3xl font-bold mb-2">Empowerment selection</h1>
      <p className="text-[#0a0d12]/60 text-sm mb-6">Draw the three monthly empowerment selected beneficiaries transparently from the verified pool.</p>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-xl border border-black/5 p-6">
          <h2 className="font-serif text-lg font-bold mb-4">Run monthly draw</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Month (YYYY-MM)</label>
              <input value={month} onChange={(e) => setMonth(e.target.value)} placeholder="2026-10" className="w-full px-3 py-2 border border-black/15 rounded-md" data-testid="selection-month" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Reserves</label>
              <input type="number" min="0" value={numReserves} onChange={(e) => setNumReserves(e.target.value)} className="w-full px-3 py-2 border border-black/15 rounded-md" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Officers</label>
              <input value={officers} onChange={(e) => setOfficers(e.target.value)} placeholder="Names, roles" className="w-full px-3 py-2 border border-black/15 rounded-md" />
            </div>
          </div>
          <button onClick={run} disabled={running} className="dk-btn-primary mt-4 disabled:opacity-60" data-testid="run-selection-btn">
            <Play size={14} /> {running ? "Running…" : "Run selection"}
          </button>
        </div>

        <div className="bg-white rounded-xl border border-black/5 p-6">
          <div className="flex items-center gap-2 mb-4"><Settings size={16} /><h2 className="font-serif text-lg font-bold">Programme settings</h2></div>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Next selection (public)</label>
              <input type="date" value={settings.next_empowerment_selection} onChange={(e) => setSettings({ ...settings, next_empowerment_selection: e.target.value })} className="w-full px-3 py-2 border border-black/15 rounded-md" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">₦1m blackout (months)</label>
              <input type="number" value={settings.ben1m_blackout_months} onChange={(e) => setSettings({ ...settings, ben1m_blackout_months: Number(e.target.value) })} className="w-full px-3 py-2 border border-black/15 rounded-md" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">₦200k blackout (months)</label>
              <input type="number" value={settings.ben200k_blackout_months} onChange={(e) => setSettings({ ...settings, ben200k_blackout_months: Number(e.target.value) })} className="w-full px-3 py-2 border border-black/15 rounded-md" />
            </div>
            <button onClick={saveSettings} className="dk-btn-primary !py-2 !px-3 text-sm w-full justify-center"><Save size={14} /> Save</button>
          </div>
        </div>
      </div>

      <h2 className="font-serif text-xl font-bold mt-10 mb-4">Previous selections</h2>
      <div className="space-y-3">
        {items.map((s) => (
          <div key={s.id} className="bg-white rounded-xl border border-black/5 p-5" data-testid={`selection-${s.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#0d1724]/60">Month</div>
                <div className="font-serif text-xl text-[#0a0d12] font-bold">{s.month}</div>
                <div className="text-xs text-[#0a0d12]/60 mt-1">Pool: {s.eligible_count} · Officers: {s.officers || "—"} · {new Date(s.created_at).toLocaleString()}</div>
              </div>
              <button onClick={() => remove(s.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
            </div>
            <div className="grid md:grid-cols-3 gap-3 mt-4">
              <Winner amount="₦1,000,000" serial={s.winner_1m} highlight />
              <Winner amount="₦200,000" serial={s.winner_200k_a} />
              <Winner amount="₦200,000" serial={s.winner_200k_b} />
            </div>
            {s.reserves && s.reserves.length > 0 && (
              <div className="mt-3 text-xs text-[#0a0d12]/70">Reserves: <span className="font-mono">{s.reserves.join(" · ")}</span></div>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-[#0a0d12]/50">No selections have been run yet.</p>}
      </div>
    </div>
  );
}

function Winner({ amount, serial, highlight }) {
  if (!serial) return null;
  return (
    <div className={`p-4 rounded-md ${highlight ? "bg-[#155e3f] text-white ring-2 ring-[#d6aa43]" : "bg-[#f8f4e8]"}`}>
      <div className="flex items-center gap-2 mb-1">
        <Trophy size={14} className={highlight ? "text-[#d6aa43]" : "text-[#0d1724]"} />
        <span className={`text-xs uppercase tracking-[0.2em] ${highlight ? "text-[#d6aa43]" : "text-[#0d1724]/70"}`}>{amount}</span>
      </div>
      <div className={`font-mono text-sm font-bold ${highlight ? "text-[#d6aa43]" : "text-[#0a0d12]"}`}>{serial}</div>
    </div>
  );
}
